import { inject } from '@angular/core';
import type { Route, Routes } from '@angular/router';

import { authGuard } from '@app/auth/guards/auth.guard';
import { projectPermissionLoader } from '@app/auth/guards/project.guard';
import { redirectToHomeGuard } from '@app/auth/guards/redirect-to-home.guard';
import {
    MODULE_DISPLAY_NAME,
    MODULE_ROUTE_PATH,
    MODULES,
    PAGE_ROUTE_PATH,
} from '@app/shared/constants/shared.constant';
import { BreadcrumbService } from '@app/shared/services/breadcrumb.service';
import { handleChunkNotFound } from '@app/shared/utils/chunk-not-found';
import type { ModuleRouteData } from '@app/shared/utils/router-factory';
import { ChunkNotFoundDialogService } from '@app/theme/services/chunk-not-found-dialog.service';

interface ModuleRoute extends Route {
    path: MODULE_ROUTE_PATH;
    data: ModuleRouteData & { breadcrumb: string };
    canMatch: [typeof authGuard];
    loadChildren: Route['loadChildren'];
}

const projectModuleRoutes: ModuleRoute[] = [
    {
        path: MODULE_ROUTE_PATH.ASSETS,
        loadChildren: () => {
            const dialogService = inject(ChunkNotFoundDialogService);
            return import('@app/modules/assets/assets.routes')
                .then(m => m.assetsRoutes)
                .catch(err => handleChunkNotFound(err, dialogService));
        },
        data: {
            breadcrumb: MODULE_DISPLAY_NAME[MODULES.ASSETS],
            moduleName: MODULES.ASSETS,
        },
        canMatch: [authGuard],
    },
    {
        path: MODULE_ROUTE_PATH.TESTING,
        loadChildren: () => {
            const dialogService = inject(ChunkNotFoundDialogService);
            return import('@app/modules/testing/testing.routes')
                .then(m => m.testingRoutes)
                .catch(err => handleChunkNotFound(err, dialogService));
        },
        data: {
            breadcrumb: MODULE_DISPLAY_NAME[MODULES.TESTING],
            moduleName: MODULES.TESTING,
        },
        canMatch: [authGuard],
    },
    {
        path: MODULE_ROUTE_PATH.MONITOR,
        loadChildren: () => {
            const dialogService = inject(ChunkNotFoundDialogService);
            return import('@app/modules/monitor/monitor.routes')
                .then(m => m.monitorRoutes)
                .catch(err => handleChunkNotFound(err, dialogService));
        },
        data: {
            breadcrumb: MODULE_DISPLAY_NAME[MODULES.MONITOR],
            moduleName: MODULES.MONITOR,
        },
        canMatch: [authGuard],
    },
    {
        path: MODULE_ROUTE_PATH.MANAGEMENT,
        loadChildren: () => {
            const dialogService = inject(ChunkNotFoundDialogService);
            return import('@app/modules/management/management.routes')
                .then(m => m.managementRoutes)
                .catch(err => handleChunkNotFound(err, dialogService));
        },
        data: {
            breadcrumb: MODULE_DISPLAY_NAME[MODULES.MANAGEMENT],
            moduleName: MODULES.MANAGEMENT,
        },
        canMatch: [authGuard],
    },
];

const extraRoutes: Routes = [
    {
        path: MODULE_ROUTE_PATH.HOME,
        loadComponent: () => {
            const dialogService = inject(ChunkNotFoundDialogService);
            return import('@app/modules/home-page/home-page.component')
                .then(m => m.HomePageComponent)
                .catch(err => handleChunkNotFound(err, dialogService));
        },
        data: {
            breadcrumb: 'Home',
        },
        canMatch: [authGuard],
    },
    {
        path: 'ai-chat',
        loadComponent: () => {
            const dialogService = inject(ChunkNotFoundDialogService);
            return import('@app/shared/components/ai-chat/ai-chat.component')
                .then(m => m.AIChatComponent)
                .catch(err => handleChunkNotFound(err, dialogService));
        },
        canActivate: [authGuard],
        data: {
            title: 'CORRA Chat',
            breadcrumb: 'CORRA Chat',
        },
    },
    {
        path: 'unauthorised',
        loadComponent: () =>
            import('@app/shared/components/forbidden/forbidden.component').then(
                m => m.ForbiddenComponent,
            ),
        pathMatch: 'full',
        data: {
            title: 'Forbidden',
        },
    },
    {
        path: 'reload',
        loadComponent: () =>
            import('@app/shared/components/splash/splash.component').then(m => m.SplashComponent),
        pathMatch: 'full',
        data: {
            title: 'Reloading...',
        },
    },
    {
        path: 'not-found',
        loadComponent: () =>
            import('@app/shared/components/not-found/not-found.component').then(
                m => m.NotFoundComponent,
            ),
        pathMatch: 'full',
        data: {
            title: 'Not Found',
        },
    },
];

export const themeRoutes: Routes = [
    {
        path: MODULE_ROUTE_PATH.SETTING,
        outlet: 'settings',
        loadChildren: () => {
            const dialogService = inject(ChunkNotFoundDialogService);
            return import('@app/modules/settings/settings-routes')
                .then(m => m.settingsRoutes)
                .catch(err => handleChunkNotFound(err, dialogService));
        },
        canMatch: [authGuard],
    },
    {
        path: '',
        loadComponent: () => import('@app/theme/theme.component').then(m => m.ThemeComponent),
        canMatch: [authGuard],
        children: [
            { path: '', redirectTo: MODULE_ROUTE_PATH.HOME, pathMatch: 'full' },
            ...projectModuleRoutes,
            ...extraRoutes,
        ],
    },
    {
        path: ':projectSlug',
        loadComponent: () => import('@app/theme/theme.component').then(m => m.ThemeComponent),
        canMatch: [authGuard, projectPermissionLoader],
        resolve: { customBreadcrumbs: BreadcrumbService },
        children: [
            {
                path: '',
                loadComponent: () => {
                    const dialogService = inject(ChunkNotFoundDialogService);
                    return import('@app/modules/project-home-page/project-home-page.component')
                        .then(m => m.ProjectHomePageComponent)
                        .catch(err => handleChunkNotFound(err, dialogService));
                },
                canMatch: [authGuard],
                pathMatch: 'full',
            },
            ...projectModuleRoutes,
            {
                path: PAGE_ROUTE_PATH.PROJECT_SETTINGS,
                loadChildren: () => {
                    const dialogService = inject(ChunkNotFoundDialogService);
                    return import('@app/modules/project-settings/project-settings.route')
                        .then(m => m.projectSettingsRoutes)
                        .catch(err => handleChunkNotFound(err, dialogService));
                },
                canMatch: [authGuard],
            },
            {
                path: 'unauthorised',
                loadComponent: () =>
                    import('@app/shared/components/forbidden/forbidden.component').then(
                        m => m.ForbiddenComponent,
                    ),
                pathMatch: 'full',
                data: {
                    title: 'Forbidden',
                },
            },
        ],
    },
    {
        /* This route will be activated when no route matches in all the routing modules and
           redirects to home. The component here is never actually used. If:
           - User is not logged in - authGuard will redirect to /login
           - User is logged in - redirectToHomeGuard will redirect to home
        */
        path: '**',
        loadComponent: () =>
            import('@app/auth/components/login/login.component').then(m => m.LoginComponent),
        pathMatch: 'full',
        canActivate: [authGuard, redirectToHomeGuard],
    },
];
